/**
 * What the organization may record right now: the same answer the recorder
 * receives, read here so the dashboard shows the state a person would
 * otherwise only find in a browser console.
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { LimitReason, Meter, SdkLimits } from "@backstory/protocol";
import { request } from "../../api";
import { useCan } from "../../auth/roles";
import { billingKeys } from "./api";

export type OrgLimits = SdkLimits;

const enc = encodeURIComponent;

export const limitsApi = {
  get: (org: string) => request<OrgLimits>(`/v1/orgs/${enc(org)}/billing/limits`),
};

/**
 * Loads the organization's limits. Undefined while loading and when the
 * request fails: an unknown state is shown as nothing rather than as a stop.
 */
export function useOrgLimits(orgId: string | undefined): OrgLimits | undefined {
  const q = useQuery({
    queryKey: billingKeys.limits(orgId ?? ""),
    queryFn: () => limitsApi.get(orgId!),
    enabled: !!orgId,
    retry: 0,
    refetchInterval: 60_000,
  });
  return q.data;
}

/** Why a feature is restricted, said the way it appears after "Stopped:". */
export function limitReasonLabel(reason: LimitReason | undefined): string {
  switch (reason) {
    case "cap_reached":
      return "cap reached";
    case "payment_overdue":
      return "payment overdue";
    case "trial_ceiling":
      return "trial limit reached";
    case "subscription_ended":
      return "subscription ended";
    case "not_included":
      return "not included on this plan";
    case "disabled":
      return "turned off";
    default:
      return "paused";
  }
}

export interface RecordingInstruction {
  /** What pages with this key are told to record, as a short badge. */
  label: string;
  tone: "ok" | "warn" | "neutral" | "danger";
  /** Set when a plan limit or account state narrowed the saved setting. */
  narrowedBy?: string;
}

/**
 * The instruction the recorder receives for one app: the saved recording
 * setting, narrowed by the organization's limits the same way the config
 * endpoint narrows it. The badge on the keys page, the staff customer
 * record, and the limits banner all read this, so they cannot disagree.
 */
export function recordingInstruction(
  recording: { mode: "always" | "conditional" | "triggers-only" | "off"; baselineSampleRate: number },
  limits: { sessions: "ok" | "triggers-only" | "off"; sessionsReason?: string } | undefined,
): RecordingInstruction {
  if (recording.mode === "off") return { label: "Off", tone: "neutral" };
  if (limits?.sessions === "off") {
    const why = limitReasonLabel(limits.sessionsReason as LimitReason | undefined);
    return { label: `Off: ${why}`, tone: "danger", narrowedBy: why };
  }
  if (limits?.sessions === "triggers-only") {
    const why = limitReasonLabel(limits.sessionsReason as LimitReason | undefined);
    return { label: `Only when something goes wrong: ${why}`, tone: "warn", narrowedBy: why };
  }
  if (recording.mode === "triggers-only") return { label: "Only when something goes wrong", tone: "warn" };
  if (recording.mode === "conditional") {
    const pct = Math.round(Math.min(1, Math.max(0, recording.baselineSampleRate)) * 100);
    if (pct <= 0) return { label: "Only when something goes wrong", tone: "warn" };
    if (pct >= 100) return { label: "Every visit", tone: "ok" };
    return { label: `${pct}% of visits, and when something goes wrong`, tone: "ok" };
  }
  return { label: "Every visit", tone: "ok" };
}

/**
 * The state line a usage meter shows when the feature behind it is not
 * recording normally. Empty when everything runs, and for "not included"
 * because the meter already says that from the package itself.
 */
export function meterLimitStatus(meter: Meter, limits: OrgLimits | undefined): string | undefined {
  if (!limits) return undefined;
  if (meter === "stored_sessions") {
    if (limits.sessions === "off") return `Stopped: ${limitReasonLabel(limits.sessionsReason)}`;
    if (limits.sessions === "triggers-only") return `Errors and triggers only: ${limitReasonLabel(limits.sessionsReason)}`;
    return undefined;
  }
  const f = meter === "canvas_minutes" ? limits.canvas : meter === "live_minutes" ? limits.live : meter === "custom_events" ? limits.customEvents : undefined;
  if (!f || f.allowed || f.reason === "not_included") return undefined;
  return `Stopped: ${limitReasonLabel(f.reason)}`;
}

/**
 * Whether the feature behind a meter has stopped recording entirely. A
 * stopped meter cannot grow, so a month-end projection made before the stop
 * no longer applies to it.
 */
export function meterStopped(meter: Meter, limits: OrgLimits | undefined): boolean {
  if (!limits) return false;
  if (meter === "stored_sessions") return limits.sessions === "off";
  const f = meter === "canvas_minutes" ? limits.canvas : meter === "live_minutes" ? limits.live : meter === "custom_events" ? limits.customEvents : undefined;
  return !!f && !f.allowed;
}

/** One sentence per feature that is not recording normally, for a banner. */
export function limitNotices(limits: OrgLimits | undefined): { tone: "warn" | "danger"; text: string }[] {
  if (!limits) return [];
  const out: { tone: "warn" | "danger"; text: string }[] = [];
  if (limits.sessions === "off") {
    switch (limits.sessionsReason) {
      case "subscription_ended":
        out.push({ tone: "danger", text: "Session recording is stopped: the subscription ended. Pick a plan to start recording again." });
        break;
      default:
        out.push({ tone: "danger", text: "Session recording is stopped: this month's session cap is reached. Raise the cap or pick a larger plan, or wait for the period to reset." });
    }
  } else if (limits.sessions === "triggers-only") {
    switch (limits.sessionsReason) {
      case "payment_overdue":
        out.push({ tone: "warn", text: "Only sessions with errors or triggers are being recorded until the payment is settled." });
        break;
      case "trial_ceiling":
        out.push({ tone: "warn", text: "Only sessions with errors or triggers are being recorded: the trial limit is reached. Pick a plan to record everything." });
        break;
      default:
        out.push({ tone: "warn", text: "Only sessions with errors or triggers are being recorded: this month's session cap is reached." });
    }
  }
  if (!limits.canvas.allowed && limits.canvas.reason && limits.canvas.reason !== "not_included" && limits.canvas.reason !== "disabled") {
    out.push({ tone: "warn", text: `Canvas recording is stopped: ${limitReasonLabel(limits.canvas.reason)}.` });
  }
  if (!limits.live.allowed && limits.live.reason && limits.live.reason !== "not_included" && limits.live.reason !== "disabled") {
    out.push({ tone: "warn", text: `Live Assist is stopped: ${limitReasonLabel(limits.live.reason)}.` });
  }
  if (limits.customEvents && !limits.customEvents.allowed && limits.customEvents.reason && limits.customEvents.reason !== "not_included") {
    out.push({ tone: "warn", text: "Custom events are stopped for the rest of the month. Recording continues." });
  }
  return out;
}

/**
 * Says what is not recording right now and where to change that. Renders
 * nothing while everything runs, so pages can place it unconditionally.
 */
export function LimitsBanner({ orgId, limits }: { orgId: string; limits: OrgLimits | undefined }) {
  const can = useCan();
  const notices = limitNotices(limits);
  if (notices.length === 0) return null;
  const tone = notices.some((n) => n.tone === "danger") ? "danger" : "warn";
  return (
    <div className={`banner ${tone}`} role={tone === "danger" ? "alert" : "status"} data-testid="limits-banner">
      {notices.map((n) => (
        <div key={n.text}>{n.text}</div>
      ))}
      {can.billing && (
        <Link to="/orgs/$org/billing" params={{ org: orgId }}>
          Open billing
        </Link>
      )}
    </div>
  );
}

/** What a page says when Live Assist cannot start. Undefined when it can. */
export function liveUnavailableMessage(limits: OrgLimits | undefined): string | undefined {
  if (!limits || limits.live.allowed) return undefined;
  switch (limits.live.reason) {
    case "cap_reached":
      return "Live Assist has reached this month's cap. Raise the cap in Billing or wait for the period to reset.";
    case "subscription_ended":
      return "Live Assist is paused because the subscription ended. Pick a plan in Billing to start assisting again.";
    case "disabled":
      return "Live Assist is turned off for this organization.";
    default:
      return "Live Assist is included on Team and above.";
  }
}
